import { useEffect, useState, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { FeatureAccessProvider } from "@/components/UpgradePrompt";
import { FeatureFlagProvider } from "@/components/FeatureFlag";
import { CompanySettingsProvider } from "@/hooks/useCompanySettings";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import ViewAsConsumePage from "@/pages/ViewAsConsumePage";
import JoinPhygitalPage from "@/pages/JoinPhygitalPage";
import BrandOnePagerPage from "@/pages/BrandOnePagerPage";
import AuthCallback from "@/pages/AuthCallback";
// WalletPage / CustomerDashboard moved to /pages/_legacy (Iter 9.47.q cleanup)
// ProviderDashboard - wrapper that redirects to unified dashboard
import ProviderDashboard from "@/pages/ProviderDashboard";
import StylistPublicProfile from "@/pages/StylistPublicProfile";
import ClientCheckin from "@/pages/ClientCheckin";
import CheckinConfirmed from "@/pages/CheckinConfirmed";
import PlanExperience from "@/pages/PlanExperience";
import MePlanPage from "@/pages/MePlanPage";
import MePlanLookPage from "@/pages/MePlanLookPage";
import BrandPilotSharePage from "@/pages/BrandPilotSharePage";
import PreferenceCardPublicPage from "@/pages/PreferenceCardPublicPage";
import StatusPage from "@/pages/StatusPage";
import PortfolioPage from "@/pages/PortfolioPage";
import AdminHostDashboardSetupPage from "@/pages/admin/AdminHostDashboardSetupPage";
import AdminUserNotesPage from "@/pages/admin/AdminUserNotesPage";
import ChatInbox from "@/pages/ChatInbox";
import InspirationsPage from "@/pages/InspirationsPage";
import PersonalizePage from "@/pages/PersonalizePage";
import TechnicianDashboard from "@/pages/TechnicianDashboard";
// AppointmentBoardPage moved to /pages/_legacy (Iter 9.47.q cleanup)
import ColorsPage from "@/pages/ColorsPage";
import PricingPage from "@/pages/PricingPage";
import PublicOnboardPage from "@/pages/PublicOnboardPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import SubscriptionSuccessPage from "@/pages/SubscriptionSuccessPage";
import AdminDashboard from "@/pages/admin/AdminDashboardNew";
import SignalsPage from "@/pages/SignalsPage";
import ExperiencesPage from "@/pages/ExperiencesPage";
import BoardReviewPage from "@/pages/BoardReviewPage";
import ShareProfilePage from "@/pages/ShareProfilePage";
import SharePreferenceCardPage from "@/pages/SharePreferenceCardPage";
import PreferenceShareCardPage from "@/pages/PreferenceShareCardPage";
import PublicProfilePage from "@/pages/PublicProfilePage";

// Smart router for /p/:shareId - shr_* / share_* ids hit the new share card; everything else falls back to the legacy public profile.
function SmartShareRouter() {
  const { shareId } = useParams();
  if (shareId && (shareId.startsWith("shr_") || shareId.startsWith("share_"))) {
    return <PreferenceShareCardPage />;
  }
  return <PublicProfilePage />;
}
import HelpPage from "@/pages/HelpPage";
import SupportIssuePage from "@/pages/SupportIssuePage";
import SupportCategoryPage from "@/pages/SupportCategoryPage";
import TipTechPage from "@/pages/TipTechPage";
import EventPageElegant from "@/pages/EventPageElegant";
import HostInviteRsvpPage from "@/pages/HostInviteRsvpPage";
import EventPassPage from "@/pages/EventPassPage";
import DeveloperPage from "@/pages/DeveloperPage";
import HostDashboardV2 from "@/pages/HostDashboardV2";
import HostMediaLibraryPage from "@/pages/HostMediaLibraryPage";
import AdminControlPage from "@/pages/AdminControlPage";
import MyOccasionsPage from "@/pages/MyOccasionsPage";
import { AdminCopyProvider } from "@/contexts/AdminCopyContext";
import { useLiveCategoryMeta } from "@/hooks/useLiveCategoryMeta";
import { useColorTokens } from "@/hooks/useColorTokens";
import HostSignupPage from "@/pages/HostSignupPage";
import AdvocatePortalPage from "@/pages/AdvocatePortalPage";
import UserProfilePage from "@/pages/UserProfilePage";
import PreferenceSetupPage from "@/pages/PreferenceSetupPage";
import DynamicPreferencesDemoPage from "@/pages/DynamicPreferencesDemoPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
// SharedExperiencePage moved to /pages/_legacy (Iter 9.47.q cleanup)
import PublicPreferenceCardPage from "@/pages/PublicPreferenceCardPage";
import EventSummaryPage from "@/pages/EventSummaryPage";
import EventsSalesPage from "@/pages/EventsSalesPage";
import MUASalesPage from "@/pages/MUASalesPage";
import HairSalesPage from "@/pages/HairSalesPage";
import NailSalesPage from "@/pages/NailSalesPage";
import HomePage from "@/pages/HomePage";
import ProviderSetupPage from "@/pages/ProviderSetupPage";
import HostSetupPage from "@/pages/HostSetupPage";
import CreateEventPage from "@/pages/CreateEventPage";
import PitchDeckPage from "@/pages/PitchDeckPage";
import AppTourPage from "@/pages/AppTourPage";
import PreferenceCollectPage from "@/pages/PreferenceCollectPage";
import BusinessPlanPage from "@/pages/BusinessPlanPage";
import FrontRowPage from "@/pages/FrontRowPage";
// PartnerLandingPage removed - /hthfoverview retired
import PartnerPitchPage from "@/pages/PartnerPitchPage";
import EventPilotPage from "@/pages/EventPilotPage";
// InvitePage moved to /pages/_legacy (Iter 9.47.q cleanup)
import EventWaitlistPage from "@/pages/EventWaitlistPage";
import EventSharePage from "@/pages/EventSharePage";
import MagicLinkCallback from "@/pages/MagicLinkCallback";
import ComingSoonPage from "@/pages/ComingSoonPage";
import ConfirmationPage from "@/pages/ConfirmationPage";
import PreferenceCardStagePage from "@/pages/PreferenceCardStagePage";
import JoinTeamPage from "@/pages/JoinTeamPage";
import JoinPartnerPage from "@/pages/JoinPartnerPage";
import VipClaimPage from "@/pages/VipClaimPage";
// ProductRoadmapPage / AdminCleanupPage moved to /pages/_legacy (Iter 9.47.q cleanup)
import TapInPage from "@/pages/TapInPage";
import PilotPortalPage from "@/pages/PilotPortalPage";
import PilotInvitePage from "@/pages/PilotInvitePage";
import PilotNDAPage from "@/pages/PilotNDAPage";
import PilotWelcomePage from "@/pages/PilotWelcomePage";
import PilotConfirmationPage from "@/pages/PilotConfirmationPage";
import PilotPortalDashboard from "@/pages/PilotPortalDashboard";
import InvestorPortalPage from "@/pages/InvestorPortalPage";

// SupportPortalPage moved to /pages/_legacy (Iter 9.47.q cleanup)
import PortalPage from "@/pages/PortalPage";
import VisualIntakeBuilder from "@/pages/VisualIntakeBuilder";
import ModernIntakePage from "@/pages/ModernIntakePage";
import WellnessSalesPage from "@/pages/WellnessSalesPage";
import InstructorSalesPage from "@/pages/InstructorSalesPage";
import TravelSalesPage from "@/pages/TravelSalesPage";
import DispensariesSalesPage from "@/pages/DispensariesSalesPage";
import CulinarySalesPage from "@/pages/CulinarySalesPage";
import RetailSalesPage from "@/pages/RetailSalesPage";
import LashSalesPage from "@/pages/LashSalesPage";
import DesignPreview from "@/pages/design/DesignPreview";
import PhygitalPlanMakeup from "@/pages/phygital/PhygitalPlanMakeup";
import PhygitalComponentLibrary from "@/pages/phygital/PhygitalComponentLibrary";
import BrandPortalSalesPage from "@/pages/BrandPortalSalesPage";
import CheckInKioskPage from "@/pages/CheckInKioskPage";
import CommunityDemo from "@/pages/CommunityDemo";
import InstructorPilotPage from "@/pages/InstructorPilotPage";
// InstructorIntakePage removed - using EventPageElegant with instructorMode per Master Plan
import InstructorInsightsPage from "@/pages/InstructorInsightsPage";
import PlanningExperienceDemo from "@/pages/PlanningExperienceDemo";
// SettingsPage moved to /pages/_legacy/ (Iter 9.47.q cleanup) - settings live inside /me Settings tab
// PortalWelcomePage / PortalNDAPage aliases removed - canonical PilotWelcomePage / PilotNDAPage still routed
// import ProductDashboard from "@/pages/ProductDashboard"; // Temporarily disabled - needs Notion sync first

// Components
import HelpButton from "@/components/HelpButton";
import OrgInvitePopup from "@/components/OrgInvitePopup";
// Removed: SyncIndicator (live syncing UI deprecated)
import { AdminProvider, AdminToolbar } from "@/components/AdminContentEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Set up axios interceptors for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('session_token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Always include credentials
    config.withCredentials = true;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on login/auth endpoints or admin page - let them handle their own errors
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    const isAdminPage = window.location.pathname.includes('/admin');
    if (error.response?.status === 401 && !isAuthEndpoint && !isAdminPage) {
      // Token might be expired - clear it
      const currentToken = localStorage.getItem('session_token');
      if (currentToken) {
        console.log('Session expired, clearing token');
        localStorage.removeItem('session_token');
        // Only redirect if we're not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(null);

  // Fetch fresh user data from server
  const fetchUserData = async () => {
    try {
      const storedToken = localStorage.getItem('session_token');
      if (!storedToken) {
        return null;
      }
      const response = await axios.get(`${API}/auth/me`);
      return response.data;
    } catch (error) {
      console.log('Fetch user data failed:', error.response?.status);
      return null;
    }
  };

  const checkAuth = async () => {
    try {
      // Check if we have a stored token first
      const storedToken = localStorage.getItem('session_token');
      console.log('checkAuth - token exists:', !!storedToken);
      if (!storedToken) {
        // No token stored, user is not authenticated
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
      
      // Token exists, verify it with the server
      // The axios interceptor will automatically add the Authorization header
      console.log('checkAuth - calling /api/auth/me');
      const response = await axios.get(`${API}/auth/me`);
      console.log('checkAuth - got response:', response.data?.email);
      setUser(response.data);
      setIsAuthenticated(true);
      // Iter 9.30: auto-detect timezone on first login if user hasn't set one yet.
      // We don't override an existing setting - only fill it in once.
      try {
        if (!response.data?.timezone) {
          const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (detected) {
            await axios.patch(`${API}/users/me/settings`, { timezone: detected });
            setUser({ ...response.data, timezone: detected });
          }
        }
      } catch (tzErr) {
        console.warn("timezone auto-detect skipped:", tzErr?.message);
      }
    } catch (error) {
      // Token is invalid or expired - clear it
      console.log('Auth check failed:', error.response?.status, error.message);
      localStorage.removeItem('session_token');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Manual user data refresh (called after mutations such as profile updates)
  // Silent: no UI sync indicator, no polling.
  const refreshUser = async () => {
    const userData = await fetchUserData();
    if (userData) {
      setUser(userData);
      // Dispatch custom event for components that need to know about updates
      window.dispatchEvent(new CustomEvent('phygital:user-updated', { detail: userData }));
      return userData;
    }
    return null;
  };

  // Update specific user fields without full refresh (for optimistic updates)
  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
    // Also dispatch event
    window.dispatchEvent(new CustomEvent('phygital:user-updated', { detail: { ...user, ...updates } }));
  };

  const login = (userData) => {
    // Store session token in localStorage as cookie fallback
    if (userData.session_token) {
      localStorage.setItem('session_token', userData.session_token);
    }
    setUser(userData);
    setIsAuthenticated(true);
  };

  const logout = async () => {
    // Iter 9.30: clear local state FIRST, then attempt server-side revocation.
    // If the server fails (network, 401, etc.) the client has already signed out
    // so the user never gets stuck in a broken half-logged-in state.
    const token = localStorage.getItem('session_token');
    localStorage.removeItem('session_token');
    setUser(null);
    setIsAuthenticated(false);
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (e) {
        console.warn("Logout server revocation failed (already signed out client-side):", e?.message);
      }
    }
    // Hard navigate to landing so React state fully resets and the user sees the
    // signed-out experience without any cached pages flashing
    window.location.assign('/');
  };

  useEffect(() => {
    // Skip auth check for authentication callbacks - they handle their own auth
    const path = window.location.pathname;
    console.log('AuthProvider checking path:', path);
    if (path === '/auth/pilot' || path === '/auth/magic') {
      console.log('Skipping auth check for auth callback');
      setLoading(false);
      return;
    }
    
    // If we have a session token, always verify it
    const hasToken = localStorage.getItem('session_token');
    if (hasToken && isAuthenticated === null) {
      console.log('Token exists, verifying auth...');
    }
    
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      isAuthenticated, 
      login, 
      logout, 
      checkAuth, 
      refreshUser, 
      updateUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // If loading or isAuthenticated is null (initial state), wait for auth check
  if (loading || isAuthenticated === null) {
    // Check if we have a session token but auth isn't determined yet
    const hasToken = localStorage.getItem('session_token');
    if (hasToken) {
      // Show loading while auth state is being determined
      return (
        <div className="min-h-screen flex items-center justify-center bg-white">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
            <p className="text-gray-500 font-mono text-sm">Verifying session...</p>
          </div>
        </div>
      );
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
          <p className="text-gray-500 font-mono text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (location.state?.user) {
    return children;
  }

  if (!isAuthenticated) {
    // During pilot mode, redirect to Coming Soon instead of login
    return <Navigate to="/" replace />;
  }

  return children;
};

// PILOT MODE - Approved users only
const PILOT_APPROVED_EMAILS = [
  'me@iamnaomithomas.com',
  'kelly@oceanlifestyle.org',
  // Seeded Pilot Hosts (11 dashboards, 10 hosts)
  'aria@ariajamesbeauty.com',        // Makeup + Instructors (LA)
  'marcus@chefmarcuskitchen.com',    // Culinary (Charlotte)
  'zuri@zurihairlab.com',            // Hair (Miami)
  'tiana@luxenails.com',             // Nails (London)
  'diamond@diamondlash.com',         // Lashes (New Jersey)
  'nadia@coleevents.com',            // Events (Paris)
  'simone@hartwellness.com',         // Wellness (NYC)
  'jordan@blaketravel.com',          // Travel (Barcelona)
  'sage@greenleafco.com',            // Dispensary (Cape Town)
  'camille@duvalstyle.com',          // Retail (Milan)
  // Test account for development
  'test@test.com',
  // Legacy test users
  'makeup.host@test.phygital.com',
  'baker.host@test.phygital.com',
  'hair.host@test.phygital.com',
  'investor@test.phygital.com',
];

// Pilot type definitions for access control
// Beauty pilots (hair, nail, makeup, wellness) = full access to Home, Demos, /me, Host Dashboard
// Event/Instructor pilots = limited access (Coming Soon + Confirmation + Host pages only)
const BEAUTY_PILOT_TYPES = ['hair', 'nail', 'makeup', 'wellness', 'mua', 'lash'];
const LIMITED_PILOT_TYPES = ['event', 'instructor'];

// Check if user is a team member (stored in localStorage after accepting invite)
const isTeamMember = () => {
  try {
    const teamEvents = JSON.parse(localStorage.getItem('team_events') || '[]');
    return teamEvents.length > 0;
  } catch {
    return false;
  }
};

// Get user's pilot type from localStorage or user data
const getUserPilotType = (user) => {
  try {
    // Check localStorage for pilot type set during magic link auth
    const storedPilotType = localStorage.getItem('pilot_type');
    if (storedPilotType) return storedPilotType.toLowerCase();
    
    // Check user data for pilot type
    if (user?.pilot_type) return user.pilot_type.toLowerCase();
    if (user?.role) return user.role.toLowerCase();
    
    // Default to beauty for approved users (super admin)
    return 'beauty';
  } catch {
    return null;
  }
};

// Check if user has beauty pilot access (full platform access)
const hasBeautyPilotAccess = (user) => {
  // Super admin / admin role always has full access
  if (user?.role === 'super_admin' || user?.role === 'admin' || user?.is_super_admin) {
    return true;
  }
  const userEmail = user?.email?.toLowerCase();
  if (userEmail && PILOT_APPROVED_EMAILS.includes(userEmail)) {
    return true;
  }
  
  const pilotType = getUserPilotType(user);
  return pilotType && BEAUTY_PILOT_TYPES.includes(pilotType);
};

// Check if user has event/instructor pilot access (limited)
const hasEventPilotAccess = (user) => {
  const pilotType = getUserPilotType(user);
  return pilotType && LIMITED_PILOT_TYPES.includes(pilotType);
};

// ============================================================================
// /ME ACCESS CONTROL - Per Master Plan: Instructor Pilot doesn't get /me access
// ============================================================================

// Coming Soon Block Component for /me page
const MePageComingSoon = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 px-4">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Coming Soon</h2>
        <p className="text-gray-600 mb-6">
          Your personal profile and preferences dashboard is being built just for you. We'll notify you when it's ready!
        </p>
        <p className="text-sm text-gray-500 mb-6">
          In the meantime, you can access your event confirmations and host dashboard from the links provided.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate('/home')}
            className="px-6 py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

// Me Page Access Control - Shows Coming Soon for Instructor/Event pilots
const MePageAccessControl = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Handle admin impersonation token - auto-login with the temp session
  useEffect(() => {
    const impToken = searchParams.get('imp_token');
    if (impToken) {
      // Save the current admin token so we can restore it when exiting demo mode
      const currentToken = localStorage.getItem('session_token');
      if (currentToken && currentToken !== impToken) {
        localStorage.setItem('original_admin_token', currentToken);
      }
      localStorage.setItem('session_token', impToken);
      localStorage.setItem('impersonating', 'true');
      window.location.href = '/me';
    }
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // Preview mode users see a friendly message
  if (isPreviewMode() && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-50 px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Account Required</h2>
          <p className="text-gray-600 mb-6">
            This page requires an account. In preview mode, you can explore our landing pages and demos.
          </p>
          <button
            onClick={() => navigate('/home')}
            className="px-6 py-3 bg-purple-600 text-white rounded-full font-medium hover:bg-purple-700 transition-colors"
          >
            Explore Features
          </button>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to home
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Super admin role always has full access
  if (user?.role === 'super_admin' || user?.role === 'admin' || user?.is_super_admin) {
    return children;
  }

  // Approved pilot emails have full access
  const userEmail = user?.email?.toLowerCase();
  if (userEmail && PILOT_APPROVED_EMAILS.includes(userEmail)) {
    return children;
  }

  // Check if user has beauty pilot access (full /me access)
  if (hasBeautyPilotAccess(user)) {
    return children;
  }

  // Event/Instructor pilots see Coming Soon for /me
  // Per Master Plan Section 25: Instructors get read-only insights, NOT full dashboard
  if (hasEventPilotAccess(user)) {
    return <MePageComingSoon />;
  }

  // Team members get access
  if (isTeamMember()) {
    return children;
  }

  // Default: Allow all authenticated users during app tour captures
  return children;
};

// Pilot Protected Route - Only for approved users during pilot mode
const PilotProtectedRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
          <p className="text-gray-500 font-mono text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // During pilot mode, redirect to Coming Soon instead of login
    // Users must receive a magic link to access protected routes
    return <Navigate to="/" replace />;
  }

  // Check if user is in approved list, has admin role, OR is a team member
  const userEmail = user?.email?.toLowerCase();
  const isApproved = (userEmail && PILOT_APPROVED_EMAILS.includes(userEmail)) || 
    user?.role === 'super_admin' || user?.role === 'admin' || user?.is_super_admin;
  const hasTeamAccess = isTeamMember();
  const hasPilotTag = user?.tags?.includes('me_pilot') || user?.roles?.includes('advocate');
  // Also allow any user flagged as a pilot/host (legacy_pilot_free or is_host) to reach their host dashboard.
  const isPilotOrHost = user?.is_pilot === true || user?.is_host === true || user?.plan === 'legacy_pilot_free' || user?.stripe_bypass === true;

  if (!isApproved && !hasTeamAccess && !hasPilotTag && !isPilotOrHost) {
    // User is authenticated but not approved for pilot - redirect to coming soon
    return <Navigate to="/" replace />;
  }

  return children;
};

// ============================================================================
// PREVIEW MODE - Allow viewing demo pages without account creation
// ============================================================================

// Check if preview mode is active
const isPreviewMode = () => {
  try {
    const previewData = JSON.parse(localStorage.getItem('preview_mode') || '{}');
    if (!previewData.active) return false;
    
    // Check if preview hasn't expired (unless never_expires is true)
    if (!previewData.never_expires && previewData.expires_at) {
      const expiresAt = new Date(previewData.expires_at);
      if (new Date() > expiresAt) {
        localStorage.removeItem('preview_mode');
        return false;
      }
    }
    return true;
  } catch {
    return false;
  }
};

// Check if preview mode user can edit (they cannot)
const canPreviewEdit = () => {
  try {
    const previewData = JSON.parse(localStorage.getItem('preview_mode') || '{}');
    return previewData.can_edit === true;
  } catch {
    return false;
  }
};

// Public Demo Route - Allows unauthenticated access to demo/sales pages
const PublicDemoRoute = ({ children }) => {
  return children;
};

// Preview Access Route - Activates preview mode with a magic link
const PreviewAccessRoute = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [status, setStatus] = useState("activating");

  useEffect(() => {
    const activatePreview = async () => {
      // Valid preview tokens (admin can add more)
      const VALID_PREVIEW_TOKENS = [
        'demo2026',
        'investor2026', 
        'partner2026',
        'preview',
        'showme'
      ];
      
      // Also allow custom tokens from backend
      try {
        const response = await fetch(`${API}/preview/validate/${token}`);
        if (response.ok) {
          const data = await response.json();
          if (data.valid) {
            // Activate preview mode
            const previewData = {
              active: true,
              token: token,
              activated_at: new Date().toISOString(),
              expires_at: data.never_expires ? null : new Date(Date.now() + (data.expires_hours || 24) * 60 * 60 * 1000).toISOString(),
              never_expires: data.never_expires || data.expires_hours === 0,
              can_edit: data.can_edit || false,
              name: data.name || 'Preview User'
            };
            localStorage.setItem('preview_mode', JSON.stringify(previewData));
            setStatus("success");
            // Redirect to the specified page or default to /home
            const redirectTo = data.redirect_page || '/home';
            setTimeout(() => navigate(redirectTo, { replace: true }), 1500);
            return;
          }
        }
      } catch (err) {
        console.log("Backend preview validation not available, using hardcoded tokens");
      }
      
      // Fallback to hardcoded tokens - these never expire and cannot edit
      if (VALID_PREVIEW_TOKENS.includes(token?.toLowerCase())) {
        const previewData = {
          active: true,
          token: token,
          activated_at: new Date().toISOString(),
          expires_at: null,
          never_expires: true,
          can_edit: false,
          name: 'Preview User'
        };
        localStorage.setItem('preview_mode', JSON.stringify(previewData));
        setStatus("success");
        setTimeout(() => navigate('/home', { replace: true }), 1500);
      } else {
        setStatus("invalid");
      }
    };

    activatePreview();
  }, [token, navigate]);

  if (status === "activating") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-gray-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Activating preview access...</p>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Preview Mode Activated</h2>
          <p className="text-gray-600 mb-4">You can now explore Phygital's features without creating an account.</p>
          <p className="text-sm text-gray-400">Redirecting to homepage...</p>
        </div>
      </div>
    );
  }

  // Invalid token
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center max-w-md px-6">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Preview Link</h2>
        <p className="text-gray-600 mb-4">This preview link is not valid or has expired.</p>
        <a href="/" className="text-purple-600 hover:underline">Return to homepage</a>
      </div>
    </div>
  );
};

// Exit Preview Mode component
const ExitPreviewButton = () => {
  const navigate = useNavigate();
  
  if (!isPreviewMode()) return null;
  
  const handleExit = () => {
    localStorage.removeItem('preview_mode');
    navigate('/', { replace: true });
  };
  
  return (
    <div className="fixed top-4 right-4 z-[9998]">
      <div className="bg-purple-600 text-white rounded-full px-4 py-2 shadow-lg flex items-center gap-2 text-sm">
        <span className="hidden sm:inline">Preview Mode</span>
        <button 
          onClick={handleExit}
          className="bg-white/20 hover:bg-white/30 rounded-full px-2 py-0.5 text-xs"
        >
          Exit
        </button>
      </div>
    </div>
  );
};

// Preview Blocked Route - For pages preview users cannot access
const PreviewBlockedRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // Preview mode users see a friendly message instead of /me
  if (isPreviewMode() && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-50 px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Account Required</h2>
          <p className="text-gray-600 mb-6">
            This page requires an account. In preview mode, you can explore our landing pages and demos, but personal features like your profile are not available.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/home')}
              className="px-6 py-3 bg-purple-600 text-white rounded-full font-medium hover:bg-purple-700 transition-colors"
            >
              Explore Features
            </button>
            <button
              onClick={() => navigate('/hair')}
              className="px-6 py-3 bg-white text-purple-600 rounded-full font-medium border-2 border-purple-200 hover:border-purple-300 transition-colors"
            >
              View Hair Demo
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Regular auth check for non-preview users
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Beauty Pilot Protected Route - Only for beauty service pilots (full access)
const BeautyPilotRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get("preview");
  const [previewValidated, setPreviewValidated] = useState(null);

  // Validate preview token from URL query param
  useEffect(() => {
    const validatePreviewToken = async () => {
      if (!previewToken) {
        setPreviewValidated(false);
        return;
      }

      try {
        const response = await fetch(`${API}/preview/validate/${previewToken}`);
        if (response.ok) {
          const data = await response.json();
          if (data.valid) {
            // Activate preview mode
            const previewData = {
              active: true,
              token: previewToken,
              activated_at: new Date().toISOString(),
              expires_at: data.never_expires ? null : new Date(Date.now() + (data.expires_hours || 24) * 60 * 60 * 1000).toISOString(),
              never_expires: data.never_expires || data.expires_hours === 0,
              can_edit: false,
              name: data.name || 'Preview User'
            };
            localStorage.setItem('preview_mode', JSON.stringify(previewData));
            setPreviewValidated(true);
            return;
          }
        }
        setPreviewValidated(false);
      } catch {
        setPreviewValidated(false);
      }
    };

    validatePreviewToken();
  }, [previewToken]);

  if (loading || (previewToken && previewValidated === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-gray-200 border-t-black rounded-full animate-spin" />
          <p className="text-gray-500 font-mono text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Allow preview mode users to access demo pages (from localStorage or just validated)
  if (isPreviewMode() || previewValidated) {
    return children;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Beauty pilots and super admins get full access
  if (hasBeautyPilotAccess(user) || isTeamMember()) {
    return children;
  }

  // Event/instructor pilots see Coming Soon for these pages
  return <Navigate to="/" replace />;
};

// Pilot Auth Callback - Handles permanent pilot invite tokens
const PilotAuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("loading");
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double execution in strict mode
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const verifyPilotToken = async () => {
      if (!token) {
        setStatus("error");
        return;
      }

      try {
        const response = await fetch(`${API}/auth/pilot?token=${token}`, {
          credentials: "include"
        });
        
        if (response.ok) {
          const data = await response.json();
          // Store session token and update auth state
          localStorage.setItem("session_token", data.session_token);
          // Call login to update auth context
          login(data);
          // Small delay to ensure state updates
          await new Promise(resolve => setTimeout(resolve, 300));
          // Use navigate instead of window.location.href
          navigate(data.redirect_url || "/me", { replace: true });
        } else {
          setStatus("error");
        }
      } catch (error) {
        console.error("Pilot auth error:", error);
        setStatus("error");
      }
    };

    verifyPilotToken();
  }, [token, navigate, login]);

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invite</h1>
          <p className="text-gray-600 mb-4">This pilot invite link is invalid or has expired.</p>
          <a href="/login" className="text-purple-600 hover:underline">Go to Login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Verifying your pilot invite...</p>
      </div>
    </div>
  );
};

// Legacy route redirect helper
const LegacyHostRedirect = () => {
  const { eventId } = useParams();
  return <Navigate to={`/host/v2/${eventId}`} replace />;
};

// Investor landing page - redirects to investor portal
const InvestorLandingRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const findPortal = async () => {
      const token = localStorage.getItem("session_token");
      if (!token) { navigate("/pitchdeck", { replace: true }); return; }
      try {
        const res = await fetch(`${API}/api/investor/default-portal`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.portal_id) {
            navigate(`/investor/portal/${data.portal_id}`, { replace: true });
            return;
          }
        }
      } catch {}
      navigate("/pitchdeck", { replace: true });
    };
    findPortal();
  }, [navigate]);

  return null;
};

// Investor portal redirect - handles /investor/:portalId from magic links
const InvestorPortalRedirect = () => {
  const { portalId } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    navigate(`/investor/portal/${portalId}`, { replace: true });
  }, [portalId, navigate]);
  return null;
};

// Event Confirm Wrapper - handles /e/:slug/confirm?rsvp=xxx URL pattern
const EventConfirmWrapper = () => {
  const [searchParams] = useSearchParams();
  const rsvpId = searchParams.get('rsvp');
  
  if (!rsvpId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Confirmation Link</h2>
          <p className="text-gray-500">This confirmation link is missing the RSVP ID.</p>
        </div>
      </div>
    );
  }
  
  // Redirect to the canonical confirmation URL
  return <Navigate to={`/confirmation/${rsvpId}`} replace />;
};

// App Router
const AppRouter = () => {
  const location = useLocation();
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Iter 9.47.n - Stealth mode: `/` is the minimal "coming soon" page.
         Authenticated users still get redirected to /me; unauthenticated
         visitors see a clean white-canvas page with a host-interest popup
         + sign-in popup launchers. The full LandingPage is reachable at
         `/landing-preview` for internal preview only. */}
      <Route path="/" element={<ComingSoonPage />} />
      <Route path="/landing-preview" element={<LandingPage />} />
      
      {/* Preview Mode - Magic link for demo access without account */}
      <Route path="/preview/:token" element={<PreviewAccessRoute />} />
      <Route path="/demo/:token" element={<PreviewAccessRoute />} />
      
      {/* Home page - requires authentication */}
      <Route path="/home" element={
        <BeautyPilotRoute>
          <HomePage />
        </BeautyPilotRoute>
      } />
      
      {/* Login is the public entry point during pilot */}
      <Route path="/landing" element={<LoginPage />} />
      <Route path="/public" element={<LoginPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/coming-soon" element={<ComingSoonPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/magic" element={<MagicLinkCallback />} />
      <Route path="/auth/view-as" element={<ViewAsConsumePage />} />
      <Route path="/join" element={<JoinPhygitalPage />} />
      <Route path="/host/signup/:category" element={<HostSignupPage />} />
      <Route path="/host/signup" element={<HostSignupPage />} />
      <Route path="/host/v2/:eventId/brand-onepager" element={<BrandOnePagerPage />} />
      <Route path="/b/:providerId" element={<BrandOnePagerPage />} />
      <Route path="/join/team/:token" element={<JoinTeamPage />} />
      <Route path="/join/partner/:token" element={<JoinPartnerPage />} />
      <Route path="/vip/claim/:claimToken" element={<VipClaimPage />} />
      {/* /p/:shareId smart router: shr_* -> new share card, otherwise legacy public profile */}
      <Route path="/p/:shareId" element={<SmartShareRouter />} />
      {/* Canonical preference share card (occasion / experience / brand) */}
      <Route path="/share/:shareId" element={<PreferenceShareCardPage />} />
      <Route path="/x/:shareId" element={<PublicPreferenceCardPage />} />
      <Route path="/share/card/:share_id" element={<SharePreferenceCardPage />} />
      <Route path="/stylist/:providerId" element={<StylistPublicProfile />} />
      <Route path="/checkin/:requestId" element={<ClientCheckin />} />
      <Route path="/checkin/:requestId/confirmed" element={<CheckinConfirmed />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/support/issue" element={<SupportIssuePage />} />
      <Route path="/support/:category" element={<SupportCategoryPage />} />
      <Route path="/support" element={<Navigate to="/me/settings" replace />} />
      <Route path="/portal/:portalType" element={<PortalPage />} />
      <Route path="/status" element={<StatusPage />} />
      
      
      {/* Category Sales Pages - PUBLIC, no auth required */}
      {/* Events Sales Page */}
      <Route path="/events" element={<EventsSalesPage />} />
      <Route path="/for/events" element={<EventsSalesPage />} />
      <Route path="/demos/events" element={<EventsSalesPage />} />
      
      {/* MUA Sales Page */}
      <Route path="/mua" element={<MUASalesPage />} />
      <Route path="/for/mua" element={<MUASalesPage />} />
      <Route path="/demos/mua" element={<MUASalesPage />} />
      
      {/* Hair Sales Page */}
      <Route path="/hair" element={<HairSalesPage />} />
      <Route path="/for/hair" element={<HairSalesPage />} />
      <Route path="/demos/hair" element={<HairSalesPage />} />
      
      {/* Nail Sales Page */}
      <Route path="/nail" element={<NailSalesPage />} />
      <Route path="/nails" element={<NailSalesPage />} />
      <Route path="/for/nail" element={<NailSalesPage />} />
      <Route path="/demos/nails" element={<NailSalesPage />} />
      
      {/* Wellness Sales Page */}
      <Route path="/wellness" element={<WellnessSalesPage />} />
      <Route path="/for/wellness" element={<WellnessSalesPage />} />
      <Route path="/demos/wellness" element={<WellnessSalesPage />} />
      
      {/* Instructor Sales Page */}
      <Route path="/instructor" element={<InstructorSalesPage />} />
      <Route path="/for/instructor" element={<InstructorSalesPage />} />
      <Route path="/demos/instructor" element={<InstructorSalesPage />} />
      
      {/* Travel Sales Page */}
      <Route path="/travel" element={<TravelSalesPage />} />
      <Route path="/for/travel" element={<TravelSalesPage />} />
      <Route path="/demos/travel" element={<TravelSalesPage />} />
      
      {/* Dispensaries Sales Page */}
      <Route path="/dispensaries" element={<DispensariesSalesPage />} />
      <Route path="/for/dispensaries" element={<DispensariesSalesPage />} />
      <Route path="/demos/dispensaries" element={<DispensariesSalesPage />} />
      
      {/* Culinary Sales Page */}
      <Route path="/culinary" element={<CulinarySalesPage />} />
      <Route path="/for/culinary" element={<CulinarySalesPage />} />
      <Route path="/demos/culinary" element={<CulinarySalesPage />} />
      
      {/* Retail Sales Page */}
      <Route path="/retail" element={<RetailSalesPage />} />
      <Route path="/for/retail" element={<RetailSalesPage />} />
      <Route path="/demos/retail" element={<RetailSalesPage />} />
      
      {/* Lash Sales Page */}
      <Route path="/lash" element={<LashSalesPage />} />
      <Route path="/for/lash" element={<LashSalesPage />} />
      <Route path="/demos/lash" element={<LashSalesPage />} />

      {/* Iter 9.50+ R17 - design previews for upcoming features */}
      <Route path="/design/:flow" element={<DesignPreview />} />
      <Route path="/design" element={<DesignPreview />} />

      {/* Phygital Beauty · NEW build - Plan a Makeup Experience (V1) */}
      <Route path="/plan/makeup" element={<PhygitalPlanMakeup />} />
      <Route path="/design/components" element={<PhygitalComponentLibrary />} />

      {/* Brand Portal Sales Page (unlisted) */}
      <Route path="/brands" element={<BrandPortalSalesPage />} />
      
      {/* Developer Portal */}
      <Route path="/developers" element={<DeveloperPage />} />
      
      {/* Documentation - Now in Admin Dashboard only */}
      
      {/* Legal Pages */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      
      {/* Pitch Deck - Unlisted, password protected */}
      <Route path="/pitchdeck" element={<PitchDeckPage />} />
      <Route path="/apptour" element={<AppTourPage />} />
      
      {/* Preference Collection - Post-booking redirect from Acuity/external platforms */}
      <Route path="/collect/:category" element={<PreferenceCollectPage />} />
      <Route path="/collect" element={<PreferenceCollectPage />} />
      
      {/* Business Plan - Unlisted, password protected */}
      <Route path="/bizplan" element={<BusinessPlanPage />} />
      
      {/* Front Row - University/Audience Program */}
      <Route path="/frontrow" element={<FrontRowPage />} />
      
      {/* HTHF Overview - Consumer-facing landing page */}
      {/* /hthfoverview retired (Iter 9.40) */}
      
      {/* Partner Pitch Pages - Dynamic per partner */}
      <Route path="/partner/:eventSlug/:partnerType" element={<PartnerPitchPage />} />
      
      {/* Event Pilot Page - HTHF Pilot Program (Private - Access controlled in super admin) */}
      <Route path="/pilot" element={<EventPilotPage />} />
      <Route path="/pilot-event" element={<EventPilotPage />} />
      <Route path="/pilot-event/:pilotId" element={<EventPilotPage />} />
      <Route path="/pilot/event/:eventSlug" element={<EventPageElegant />} />
      <Route path="/event-pilot" element={<EventPilotPage />} />
      <Route path="/eventpilot" element={<EventPilotPage />} />
      
      {/* Pilot Portal Pages - Unlisted, private pilot invite pages */}
      <Route path="/pilot/:pilotType" element={<PilotPortalPage />} />
      <Route path="/portal/:pilotType" element={<PilotPortalPage />} />
      
      {/* Pilot Invite Page - Token-based invite with RSVP form */}
      <Route path="/pilot/invite/:token" element={<PilotInvitePage />} />
      
      {/* Pilot Welcome Page - After invite acceptance, before NDA */}
      <Route path="/pilot/welcome/:portalId" element={<PilotWelcomePage />} />
      
      {/* Pilot NDA Signing Page */}
      <Route path="/pilot/nda/:portalId" element={<PilotNDAPage />} />
      
      {/* Dynamic Intake Page - Template-driven */}
      <Route path="/pilot/intake/:portalId" element={<ModernIntakePage />} />
      
      {/* Legacy Visual Intake Builder - Keep for backwards compatibility */}
      <Route path="/pilot/intake-legacy/:portalId" element={<VisualIntakeBuilder />} />
      
      {/* Pilot Confirmation Page - After intake completion */}
      <Route path="/pilot/confirmation/:portalId" element={<PilotConfirmationPage />} />
      
      {/* Pilot Portal Dashboard - After NDA, shows intake and tabs */}
      <Route path="/pilot/portal/:portalId" element={<PilotPortalDashboard />} />
      
      {/* Investor Portal Dashboard */}
      <Route path="/investor/portal/:portalId" element={<AdminProvider><InvestorPortalPage /><AdminToolbar /></AdminProvider>} />
      
      {/* Investor Portal - direct ID route (from magic links) */}
      <Route path="/investor/:portalId" element={<InvestorPortalRedirect />} />
      
      {/* Investor Landing - Redirect to latest investor portal or coming soon */}
      <Route path="/investor" element={<InvestorLandingRedirect />} />
      
      {/* Portal Invite - Uses updated PilotInvitePage with demo content */}
      <Route path="/portal/invite/:token" element={<PilotInvitePage />} />
      
      {/* Tap In Integration Page */}
      <Route path="/tap-in" element={<TapInPage />} />
      <Route path="/tapin" element={<TapInPage />} />
      
      {/* Pilot Auth Callback - Permanent invite links */}
      <Route path="/auth/pilot" element={<PilotAuthCallback />} />
      
      {/* Invite Page - Portal/Pilot invites (unified) */}
      <Route path="/invite/:token" element={<PilotInvitePage />} />
      
      {/* Product Dashboard - Temporarily disabled, needs Notion sync first */}
      {/* <Route path="/product-dashboard" element={<ProductDashboard />} /> */}
      
      {/* Event Pages */}
      <Route path="/e/:slug/waitlist" element={<EventWaitlistPage />} />
      <Route path="/e/:eventSlug/confirm" element={<EventConfirmWrapper />} />
      <Route path="/e/:eventSlug" element={<EventPageElegant />} />
      <Route path="/rsvp/invite/:token" element={<HostInviteRsvpPage />} />
      <Route path="/event/:eventSlug" element={<EventPageElegant />} />
      <Route path="/events/:eventSlug" element={<EventPageElegant />} />
      <Route path="/pass/:eventId/:passToken" element={<EventPassPage />} />
      <Route path="/event-summary/:shareToken" element={<EventSummaryPage />} />
      
      {/* Event Share Pages (Dietary, Accessibility) */}
      <Route path="/events/:eventId/share/:shareType" element={<EventSharePage />} />
      
      {/* Unique Confirmation Page per RSVP - Public */}
      <Route path="/confirmation/:rsvpId" element={<ConfirmationPage />} />
      <Route path="/c/:cardId" element={<PreferenceCardStagePage />} />
      
      {/* NFC Check-in Kiosk */}
      <Route path="/check-in/:eventId" element={<CheckInKioskPage />} />
      <Route path="/checkin/:eventId" element={<CheckInKioskPage />} />
      
      {/* Community Demo Page */}
      <Route path="/demo/communities" element={<CommunityDemo />} />
      <Route path="/pilot/instructor" element={<InstructorPilotPage />} />
      
      {/* Instructor Intake Page - Uses same elegant flow as RSVP per Master Plan */}
      <Route path="/instructor-intake/:eventId" element={<EventPageElegant instructorMode={true} />} />
      
      {/* Instructor Insights Page - Phase 1D: Read-Only Audience Insights */}
      <Route path="/instructor-insights/:eventId" element={<InstructorInsightsPage />} />
      
      {/* Event Host Dashboard - Legacy redirect to V2 */}
      <Route 
        path="/host/events/:eventId" 
        element={<LegacyHostRedirect />}
      />
      
      {/* Host Dashboard V2 - Short path */}
      <Route 
        path="/host/:eventId" 
        element={
          <PilotProtectedRoute>
            <HostDashboardV2 />
          </PilotProtectedRoute>
        } 
      />
      
      {/* Host Dashboard V2 - Full path */}
      <Route 
        path="/host/v2/:eventId" 
        element={
          <PilotProtectedRoute>
            <HostDashboardV2 />
          </PilotProtectedRoute>
        } 
      />
      
      {/* Advocate Portal */}
      <Route 
        path="/advocate" 
        element={
          <PilotProtectedRoute>
            <AdvocatePortalPage />
          </PilotProtectedRoute>
        } 
      />
      
      {/* Provider Setup */}
      <Route 
        path="/provider/setup" 
        element={
          <PilotProtectedRoute>
            <ProviderSetupPage />
          </PilotProtectedRoute>
        } 
      />
      
      {/* Host Setup */}
      <Route 
        path="/host/setup" 
        element={
          <PilotProtectedRoute>
            <HostSetupPage />
          </PilotProtectedRoute>
        } 
      />
      
      {/* Create Event/Session */}
      <Route 
        path="/host/events/new" 
        element={
          <PilotProtectedRoute>
            <CreateEventPage />
          </PilotProtectedRoute>
        } 
      />
      
      {/* User Profile Page - Access controlled per pilot type */}
      {/* Per Master Plan: Instructor Pilot users see "Coming Soon" */}
      <Route 
        path="/me" 
        element={
          <MePageAccessControl>
            <UserProfilePage />
          </MePageAccessControl>
        } 
      />
      
      {/* /me/settings redirect to /me with settings tab */}
      <Route 
        path="/me/settings" 
        element={
          <MePageAccessControl>
            <UserProfilePage defaultTab="settings" />
          </MePageAccessControl>
        } 
      />
      
      {/* /settings - Redirect to /me/settings */}
      <Route
        path="/settings"
        element={<Navigate to="/me/settings" replace />}
      />
      
      {/* Iter 9.40 Pass C - unified /me/plan (single service OR occasion bundle).
          Replaces the legacy /plan-experience hardcoded EVENT_TYPES flow. */}
      <Route
        path="/me/plan"
        element={
          <ProtectedRoute>
            <MePlanPage />
          </ProtectedRoute>
        }
      />

      {/* Iter 9.40 Pass C - Retail-as-look branch (Style category only). */}
      <Route
        path="/me/plan/look"
        element={
          <ProtectedRoute>
            <MePlanLookPage />
          </ProtectedRoute>
        }
      />

      {/* Iter 9.40 Pass D - public brand pilot quarterly share page */}
      <Route
        path="/brand-share/:attributionCode"
        element={<BrandPilotSharePage />}
      />

      {/* Iter 9.40 Pass I - Canonical public preference-card share page */}
      <Route path="/preference-share/:cardId" element={<PreferenceCardPublicPage />} />
      {/* Planning Experience Demo - Public sales page */}
      <Route
        path="/plan"
        element={<PlanningExperienceDemo />}
      />
      
      {/* Iter 9.40 - /me/preferences keeps the existing UserProfilePage chrome
          (Dashboard / Defaults / Inspo / Experiences tabs). The dynamic
          preferences schema is now sourced from /api/me/preferences/schema
          and consumed by the existing Defaults tab - NO standalone screen.
          Legacy redirect for /preferences kept below. */}
      <Route
        path="/me/preferences"
        element={
          <MePageAccessControl>
            <UserProfilePage defaultTab="defaults" />
          </MePageAccessControl>
        }
      />

      {/* Iter 9.40 Pass K - direct route to Experiences tab */}
      <Route
        path="/me/experiences"
        element={
          <MePageAccessControl>
            <UserProfilePage defaultTab="experiences" />
          </MePageAccessControl>
        }
      />

      {/* Iter 9.40 Pass A.12 - Admin Host Dashboard Setup wizard
          (configure inactive dashboards before activation) */}
      <Route
        path="/admin/people/host-dashboards/:userId/setup"
        element={<AdminHostDashboardSetupPage />}
      />

      {/* Iter 9.40 Pass A.9 - Admin notes roll-up timeline */}
      <Route
        path="/admin/users/:userId/notes"
        element={<AdminUserNotesPage />}
      />

      {/* Preference Setup Page */}
      <Route 
        path="/preferences/setup" 
        element={
          <PreviewBlockedRoute>
            <ProtectedRoute>
              <PreferenceSetupPage />
            </ProtectedRoute>
          </PreviewBlockedRoute>
        } 
      />

      {/* Pass 3 - Schema-driven preferences demo */}
      <Route
        path="/preferences/dynamic/:category"
        element={<DynamicPreferencesDemoPage />}
      />
      
      {/* Protected routes */}
      
      {/* Legacy redirects - these pages now redirect to /me */}
      <Route path="/dashboard" element={<Navigate to="/me" replace />} />
      <Route path="/board" element={<Navigate to="/me" replace />} />
      
      <Route 
        path="/plan-experience" 
        element={
          <ProtectedRoute>
            <PlanExperience />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/provider" 
        element={
          <ProtectedRoute>
            <ProviderDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/provider/setup" 
        element={
          <ProtectedRoute>
            <ProviderDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Legacy redirects */}
      <Route path="/wallet" element={<Navigate to="/me" replace />} />
      <Route path="/preferences" element={<Navigate to="/me" replace />} />
      
      <Route 
        path="/board/review" 
        element={
          <ProtectedRoute>
            <BoardReviewPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/share/:category?" 
        element={
          <ProtectedRoute>
            <ShareProfilePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/portfolio" 
        element={
          <ProtectedRoute>
            <PortfolioPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/experiences" 
        element={
          <ProtectedRoute>
            <ExperiencesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/chat" 
        element={
          <ProtectedRoute>
            <ChatInbox />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/inspirations" 
        element={
          <ProtectedRoute>
            <InspirationsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/personalize" 
        element={
          <ProtectedRoute>
            <PersonalizePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/technician" 
        element={
          <ProtectedRoute>
            <TechnicianDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/colors" 
        element={
          <ProtectedRoute>
            <ColorsPage />
          </ProtectedRoute>
        } 
      />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/get-started" element={<PublicOnboardPage />} />
      <Route path="/get-started/:category" element={<PublicOnboardPage />} />
      <Route 
        path="/subscription" 
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscription/success" 
        element={
          <ProtectedRoute>
            <SubscriptionSuccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/*" 
        element={
          <PilotProtectedRoute>
            <AdminDashboard />
          </PilotProtectedRoute>
        }
      />
      <Route 
        path="/host/media" 
        element={
          <ProtectedRoute>
            <HostMediaLibraryPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/admin/control" 
        element={
          <ProtectedRoute>
            <AdminControlPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/me/occasions" 
        element={
          <ProtectedRoute>
            <MyOccasionsPage />
          </ProtectedRoute>
        }
      />
      <Route 
        path="/signals" 
        element={
          <PilotProtectedRoute>
            <SignalsPage />
          </PilotProtectedRoute>
        } 
      />
      <Route 
        path="/tip" 
        element={
          <ProtectedRoute>
            <TipTechPage />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
};

// Help Button Wrapper - DISABLED until implementation is finalized
const HelpButtonWrapper = () => {
  const location = useLocation();
  // Hide on landing page and auth pages
  const hiddenPaths = ['/', '/auth'];
  const isHidden = hiddenPaths.some(p => location.pathname === p) || location.pathname.startsWith('/auth/');
  if (isHidden) return null;
  return <HelpButton />;
};

// Org Invite Popup Wrapper - shows for authenticated users (except on admin pages)
const OrgInviteWrapper = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(true);
  
  // Don't show popup on admin pages
  const isAdminPage = location.pathname.startsWith('/admin');
  
  if (!isAuthenticated || !user || isAdminPage) return null;
  
  return showPopup ? (
    <OrgInvitePopup user={user} onClose={() => setShowPopup(false)} />
  ) : null;
};

// Exit Demo Mode Button - Shows when impersonating
const ExitDemoModeButton = () => {
  const [isImpersonating, setIsImpersonating] = useState(false);
  const { user } = useAuth();
  
  useEffect(() => {
    const flag = localStorage.getItem("impersonating") === "true";
    const originalAdminToken = localStorage.getItem("original_admin_token");
    // Only valid impersonation: flag set AND we have a stored admin token to swap back to
    setIsImpersonating(flag && !!originalAdminToken);
  }, []);
  
  // Defensive: clear stale flag if current user is admin (they can't be impersonating themselves)
  useEffect(() => {
    if (user && (user.is_admin || user.is_super_admin) && isImpersonating) {
      localStorage.removeItem("impersonating");
      localStorage.removeItem("original_admin_token");
      setIsImpersonating(false);
    }
  }, [user, isImpersonating]);
  
  const handleExitDemoMode = () => {
    const originalToken = localStorage.getItem("original_admin_token");
    if (originalToken) {
      localStorage.setItem("session_token", originalToken);
      localStorage.removeItem("impersonating");
      localStorage.removeItem("original_admin_token");
      window.location.href = "/admin/users";
    }
  };
  
  // Hide for admins (they can't be in demo mode of themselves)
  if (user && (user.is_admin || user.is_super_admin)) return null;
  if (!isImpersonating) return null;
  
  return (
    <div className="fixed top-4 right-4 z-[9999]">
      <button
        onClick={handleExitDemoMode}
        className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-full shadow-lg hover:bg-amber-600 transition-colors font-medium text-sm"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
        Exit Demo Mode
      </button>
    </div>
  );
};

function App() {
  // Iter 9.38 - fire the live category meta fetch once at app boot so the
  // static categoryConfig getCategoryPreset() returns admin-overridden values
  // for every consumer (38 import sites) without refactoring each.
  useLiveCategoryMeta();
  // Iter 9.47.c - fetch Airtable Color Tokens once and inject them as
  // CSS custom properties on :root so any component can use
  // var(--btn-primary-bg-default) etc. Solves hover-text reversibility.
  useColorTokens();
  return (
    <div className="App">
      <BrowserRouter>
        <AdminCopyProvider>
        <CompanySettingsProvider>
          <AuthProvider>
            <AdminProvider>
              <FeatureAccessProvider>
              <FeatureFlagProvider>
              <AppRouter />
              <HelpButtonWrapper />
              <OrgInviteWrapper />
              <ExitDemoModeButton />
              <ExitPreviewButton />
              <AdminToolbar />
              <Toaster position="top-center" />
              </FeatureFlagProvider>
              </FeatureAccessProvider>
            </AdminProvider>
          </AuthProvider>
        </CompanySettingsProvider>
        </AdminCopyProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
